import { useState } from 'react';
import Image from 'next/image';

import { Box, Button, Flex, Heading, Text } from '@chakra-ui/react';
import { ChatIcon } from '@udacity/chakra-uds-icons';

import { ContentContainer } from '~/components/container';
import { SectionHeader } from '~/components/section-header';
import { VideoModal } from '~/components/video-modal';
import { trackEvent } from '~/features/analytics/track-event';
import {
  TestimonialCard,
  testimonialsContent,
  testimonialsItems,
} from '~/features/government/configs/testimonials-content';

import { GovernmentTestimonialsCarousel } from './government-testimonials-carousel';

export type GovernmentTestimonialsProps = {
  pretitle?: string;
  heading?: string;
  cards?: TestimonialCard[];
};

export function GovernmentTestimonials({
  pretitle = testimonialsContent.pretitle,
  heading = testimonialsContent.heading,
  cards = testimonialsItems,
}: GovernmentTestimonialsProps) {
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const [activeTitle, setActiveTitle] = useState<string>('Video');
  const [activeCaptionsLang, setActiveCaptionsLang] = useState<string | undefined>(undefined);

  function handleWatchClick(card: TestimonialCard) {
    trackEvent({
      type: 'click',
      name: 'Government CTA Clicked',
      label: 'testimonial_watch_video_clicked',
      value: card.heading,
    });
    setActiveTitle(card.heading);
    setActiveCaptionsLang(card.captionsLang);
    setActiveVideoId(card.youtubeVideoId);
  }

  // Top padding is dropped on desktop because this section sits directly below the
  // grey.50 Impact-Driven Solutions section (which already contributes 96px bottom
  // padding); without this the matching backgrounds read as one oversized gap.
  return (
    <Flex
      as='section'
      bg='grey.50'
      flexDirection='column'
      pb={{ base: '56px', md: '96px' }}
      pt={{ base: '56px', md: '0' }}
    >
      <ContentContainer contentMaxWidth='content-max-width-md'>
        <SectionHeader
          color='grey.500'
          containerProps={{ margin: '0 auto' }}
          iconSlot={<ChatIcon color='grey.500' height='18px' width='18px' />}
          subtitle={pretitle}
          titleSlot={
            <Heading
              as='h2'
              color='grey.900'
              fontFamily='heading'
              fontSize={{ base: '2.375rem', md: '3.375rem' }}
              fontWeight='600'
              letterSpacing='-0.02em'
              lineHeight='1.1'
            >
              {heading}
            </Heading>
          }
        />
      </ContentContainer>

      <Box mt={{ base: '40px', md: '56px' }}>
        <ContentContainer contentMaxWidth='content-max-width-md' mobilePadding='0'>
          <GovernmentTestimonialsCarousel
            items={cards.map((card) => ({ ...card, key: card.id }))}
            renderCard={({ item }) => <TestimonialVideoCard card={item} onWatchClick={handleWatchClick} />}
          />
        </ContentContainer>
      </Box>

      <VideoModal
        captionsLang={activeCaptionsLang}
        title={activeTitle}
        videoId={activeVideoId}
        onClose={() => setActiveVideoId(null)}
      />
    </Flex>
  );
}

function TestimonialVideoCard({
  card,
  onWatchClick,
}: {
  card: TestimonialCard;
  onWatchClick: (card: TestimonialCard) => void;
}) {
  return (
    <Box
      bg='grey.900'
      borderRadius={{ base: '0', md: '8px' }}
      height={{ base: '320px', md: '335px' }}
      mx='0'
      overflow='hidden'
      position='relative'
      width='100%'
    >
      <Image
        alt={card.thumbnailAlt}
        sizes='(max-width: 768px) 100vw, 532px'
        src={card.thumbnailSrc}
        style={{
          objectFit: 'cover',
          objectPosition: card.thumbnailPosition ?? 'center',
        }}
        fill
      />

      <Flex
        alignItems='center'
        bottom='0'
        direction={{ base: 'column', md: 'row' }}
        gap={{ base: '16px', md: '24px' }}
        justifyContent={{ base: 'center', md: 'flex-start' }}
        left='0'
        p={{ base: '48px 16px 16px', md: '32px 24px 24px' }}
        position='absolute'
        right='0'
        background={{
          base: 'linear-gradient(to top, rgba(0,0,0,0.94) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0) 100%)',
          md: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0) 100%)',
        }}
      >
        <Text
          color='white'
          flex={{ md: '1' }}
          fontFamily='heading'
          fontSize={{ base: '1rem', md: '1.25rem' }}
          fontWeight='600'
          letterSpacing={{ base: 'normal', md: '-0.01em' }}
          lineHeight={{ base: '1.1', md: '1.2' }}
          textAlign={{ base: 'center', md: 'left' }}
          width={{ base: '100%', md: 'auto' }}
        >
          {card.heading}
        </Text>

        <Button
          aria-label={`Watch video: ${card.heading}`}
          bg={{ base: 'transparent', md: 'blue.500' }}
          border={{ base: '1.33px solid', md: 'none' }}
          borderColor={{ base: 'white', md: 'transparent' }}
          borderRadius='4px'
          color='white'
          flexShrink={0}
          fontWeight='400'
          gap='12px'
          height='auto'
          leftIcon={<PlayIcon />}
          pl='16px'
          pr='24px'
          py='14px'
          width={{ base: '100%', md: 'auto' }}
          _active={{
            bg: { base: 'whiteAlpha.300', md: 'blue.700' },
          }}
          _hover={{
            bg: { base: 'whiteAlpha.200', md: 'blue.600' },
          }}
          onClick={() => onWatchClick(card)}
        >
          Watch Video
        </Button>
      </Flex>
    </Box>
  );
}

function PlayIcon() {
  return (
    <Box as='span' display='inline-flex' height='18px' position='relative' width='18px'>
      <svg fill='none' height='18' viewBox='0 0 18 18' width='18' xmlns='http://www.w3.org/2000/svg'>
        <path
          clipRule='evenodd'
          d='M9 17.25C13.5563 17.25 17.25 13.5563 17.25 9C17.25 4.44365 13.5563 0.75 9 0.75C4.44365 0.75 0.75 4.44365 0.75 9C0.75 13.5563 4.44365 17.25 9 17.25ZM7.5 6L12 9L7.5 12V6Z'
          fill='currentColor'
          fillRule='evenodd'
        />
      </svg>
    </Box>
  );
}
